/**
 * Yjs Sync Manager
 *
 * Manages Yjs document synchronization between client and server.
 * Implements the state machine for sync states and handles:
 * - Bootstrap: Initial document load from server
 * - Local updates: Batch and send to server via realtime
 * - Remote updates: Apply from realtime to local Y.Doc
 * - State vector handshake for differential sync
 *
 * @module features/collab/yjs/syncManager
 * @see SPEC-008 Requirements 2.1, 2.3, 2.4, 2.5, 2.9, 2.10, 4.5, 4.6
 */

import * as Y from 'yjs';
import { fromUint8Array, toUint8Array } from 'js-base64';
import {
  type SyncState,
  type SyncMetrics,
  type SyncEvents,
  type BootstrapResponse,
  type YjsUpdateMessage,
} from '@linkode/shared';
import { UpdateBatcher, decodeUpdateMessage } from './updateBatcher';

// ============================================================================
// Constants
// ============================================================================

/**
 * Origin symbol to mark remote updates.
 * Used to filter out echo when local Y.Doc emits 'update' event.
 */
export const REMOTE_ORIGIN = Symbol('remote');

/**
 * Default API base URL getter
 */
function getApiBaseUrl(): string {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return '';
}

// ============================================================================
// Types
// ============================================================================

/**
 * Configuration options for YjsSyncManager
 */
export interface YjsSyncManagerOptions {
  /** Batch updates within this interval (default: 50ms) */
  batchIntervalMs?: number;
  /** Compress updates larger than this (default: 1024 bytes) */
  compressionThreshold?: number;
  /** Custom API base URL (default: from VITE_API_URL) */
  apiBaseUrl?: string;
  /** Client ID for update messages (default: auto-generated) */
  clientId?: string;
}

/**
 * Send callback for outgoing Yjs updates
 */
export type SendUpdateCallback = (message: YjsUpdateMessage) => void;

/**
 * Event emitter interface for type-safe events
 */
type EventHandler<K extends keyof SyncEvents> = SyncEvents[K];

// ============================================================================
// YjsSyncManager Class
// ============================================================================

/**
 * Manages Yjs document synchronization with server.
 *
 * Lifecycle:
 * 1. Create manager with docId
 * 2. Call connect() with sendUpdate callback
 * 3. Manager bootstraps from server
 * 4. Local edits are batched and sent via callback
 * 5. Remote updates received via handleRemoteMessage()
 * 6. Call disconnect() to cleanup
 *
 * @example
 * ```typescript
 * const manager = new YjsSyncManager('doc-123', userId, sessionToken);
 *
 * await manager.connect((msg) => {
 *   // Send via realtime transport
 *   connection.send({ type: 'sync', data: msg });
 * });
 *
 * // Handle incoming messages
 * connection.onMessage((msg) => {
 *   if (msg.type === 'sync') {
 *     manager.handleRemoteMessage(msg.data as YjsUpdateMessage);
 *   }
 * });
 *
 * // Get Y.Doc for editor binding
 * const yDoc = manager.getDoc();
 *
 * // Cleanup
 * manager.disconnect();
 * ```
 */
export class YjsSyncManager {
  private readonly docId: string;
  private readonly userId: string;
  private sessionToken: string | null;
  private readonly yDoc: Y.Doc;
  private readonly batcher: UpdateBatcher;
  private readonly apiBaseUrl: string;
  private readonly clientId: string;
  private readonly handleLocalUpdateBound: (update: Uint8Array, origin: unknown) => void;

  private state: SyncState = 'disconnected';
  private sendUpdate: SendUpdateCallback | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private eventHandlers: Map<keyof SyncEvents, Set<(...args: any[]) => void>> = new Map();

  // Metrics
  private appliedUpdatesCount = 0;
  private sentUpdatesCount = 0;
  private lastUpdateAt: number | null = null;
  private snapshotVersion: string | null = null;
  private snapshotCreatedAt: number | null = null;
  private persistLag = 0;
  private offlineUpdates: Uint8Array[] = [];

  // Offline state tracking for reconnection recovery
  // Requirements 6.1, 6.2, 6.3: Preserve local edits during disconnection
  private hasConnectedOnce = false;
  private offlineEditsApplied = false;

  constructor(
    docId: string,
    userId: string,
    sessionToken: string | null,
    options: YjsSyncManagerOptions = {}
  ) {
    this.docId = docId;
    this.userId = userId;
    this.sessionToken = sessionToken;
    this.apiBaseUrl = options.apiBaseUrl ?? getApiBaseUrl();
    this.clientId =
      options.clientId ?? `${userId}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    // Create Y.Doc
    this.yDoc = new Y.Doc();

    // Create batcher with message callback
    this.batcher = new UpdateBatcher({
      intervalMs: options.batchIntervalMs,
      compressionThreshold: options.compressionThreshold,
      clientId: this.clientId,
      onMessage: (msg) => this.handleBatchedMessage(msg),
    });

    // Listen to local Y.Doc updates
    this.handleLocalUpdateBound = this.handleLocalUpdate.bind(this);
    this.yDoc.on('update', this.handleLocalUpdateBound);
  }

  // ==========================================================================
  // Public API
  // ==========================================================================

  /**
   * Connect to server and bootstrap document state.
   * Supports both initial connect and reconnection with differential sync.
   *
   * @param sendUpdate - Callback to send outgoing updates
   * @throws Error if bootstrap fails
   *
   * @see SPEC-008 Requirements 6.4, 6.5, 6.6, 6.7, 6.9
   */
  async connect(sendUpdate: SendUpdateCallback): Promise<void> {
    if (this.state !== 'disconnected') {
      console.warn(`[YjsSyncManager] Already in state: ${this.state}`);
      return;
    }

    this.sendUpdate = sendUpdate;

    // Capture isReconnection BEFORE changing state
    const isReconnection = this.hasConnectedOnce;

    this.setState('bootstrapping');

    try {
      // Use differential sync on reconnection (with existing local state)
      await this.bootstrap(isReconnection);

      // Flush offline updates captured while disconnected
      if (this.offlineUpdates.length > 0) {
        this.setState('syncing');
        this.flushOfflineUpdates();
      }

      // Mark as connected before setState so future connects are recognized as reconnections
      this.hasConnectedOnce = true;

      // Emit state change - reconnectSynced will be emitted based on isReconnection flag
      this.setStateWithReconnection('synced', isReconnection);

      // After reconnection, flush any pending local updates
      // Requirements 6.5, 6.6: Merge local changes with remote changes
      if (isReconnection) {
        this.batcher.flush();
      }
    } catch (err) {
      this.setState('error');
      const error = err instanceof Error ? err : new Error(String(err));
      this.emit('error', error);
      throw error;
    }
  }

  /**
   * Disconnect and cleanup resources.
   * Preserves local Y.Doc state for potential reconnection.
   */
  disconnect(): void {
    // Flush any pending updates
    this.batcher.flush();

    this.sendUpdate = null;
    this.setState('disconnected');
  }

  /**
   * Destroy the manager completely.
   * Call this on unmount.
   */
  destroy(): void {
    this.disconnect();
    this.batcher.destroy();
    this.yDoc.off('update', this.handleLocalUpdateBound);
    this.yDoc.destroy();
    this.eventHandlers.clear();
  }

  /**
   * Get the document ID.
   */
  getDocId(): string {
    return this.docId;
  }

  /**
   * Get the Y.Doc instance for editor binding.
   */
  getDoc(): Y.Doc {
    return this.yDoc;
  }

  /**
   * Get current sync state.
   */
  getState(): SyncState {
    return this.state;
  }

  /**
   * Get current sync metrics.
   */
  getMetrics(): SyncMetrics {
    const docState = Y.encodeStateAsUpdate(this.yDoc);
    const snapshotAge = this.snapshotCreatedAt ? Date.now() - this.snapshotCreatedAt : null;

    return {
      state: this.state,
      appliedUpdatesCount: this.appliedUpdatesCount,
      sentUpdatesCount: this.sentUpdatesCount,
      pendingUpdatesCount: this.batcher.getPendingCount(),
      persistLag: this.persistLag,
      lastUpdateAt: this.lastUpdateAt,
      docSizeBytes: docState.byteLength,
      snapshotVersion: this.snapshotVersion,
      snapshotAge,
    };
  }

  /**
   * Subscribe to sync events.
   *
   * @param event - Event name
   * @param handler - Event handler
   * @returns Unsubscribe function
   */
  on<K extends keyof SyncEvents>(event: K, handler: EventHandler<K>): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);

    return () => {
      this.eventHandlers.get(event)?.delete(handler);
    };
  }

  /**
   * Handle incoming remote Yjs update message.
   * Call this when receiving 'yjs-update' messages from realtime transport.
   *
   * @param message - The YjsUpdateMessage from remote
   */
  handleRemoteMessage(message: YjsUpdateMessage): void {
    // Ignore our own messages (echo prevention at message level)
    if (message.clientId === this.clientId) {
      return;
    }

    try {
      // Decode and decompress if needed
      const update = decodeUpdateMessage(message);

      // Apply with REMOTE_ORIGIN to prevent echo in handleLocalUpdate
      this.applyRemoteUpdate(update);

      // Update metrics
      this.emit('updateReceived', message.originalSize);
    } catch (err) {
      console.error('[YjsSyncManager] Failed to apply remote update:', err);
      const error = err instanceof Error ? err : new Error(String(err));
      this.emit('error', error);
    }
  }

  /**
   * Get current state vector for differential sync.
   * Useful for reporting to server after reconnection.
   *
   * @returns Base64-encoded state vector
   */
  getStateVector(): string {
    const sv = Y.encodeStateVector(this.yDoc);
    return fromUint8Array(sv);
  }

  /**
   * Update session token for future bootstrap/reconnect requests.
   *
   * @param token - Latest session token
   */
  setSessionToken(token: string | null): void {
    this.sessionToken = token;
  }

  /**
   * Check if there are offline edits that have been applied locally
   * but not yet synced to the server.
   *
   * @returns True if there are pending offline edits
   * @see SPEC-008 Requirements 6.2, 6.3
   */
  hasOfflineEdits(): boolean {
    return this.offlineEditsApplied;
  }

  /**
   * Check if this manager has connected at least once.
   * Useful for determining if a connect call will be initial or reconnection.
   *
   * @returns True if manager has successfully connected before
   */
  hasConnected(): boolean {
    return this.hasConnectedOnce;
  }

  /**
   * Update persist lag from server response.
   * Called when server returns lag count after processing an update.
   *
   * @param lagCount - Number of updates pending persistence
   * @see SPEC-008 Requirements 9.1, 9.2, 3.9
   */
  updatePersistLag(lagCount: number): void {
    const oldLag = this.persistLag;
    this.persistLag = lagCount;

    if (oldLag !== lagCount) {
      this.emit('persistLagChange', lagCount);
    }
  }

  /**
   * Get current persist lag count.
   *
   * @returns Number of updates pending persistence
   */
  getPersistLag(): number {
    return this.persistLag;
  }

  // ==========================================================================
  // Private Methods
  // ==========================================================================

  /**
   * Bootstrap document from server.
   * Supports both full bootstrap (first connect) and differential sync (reconnection).
   *
   * Requirements 6.4, 6.5, 6.6: On reconnection, fetch missing updates via bootstrap
   * and merge local changes with remote changes (CRDT merge).
   *
   * @param isReconnection - Whether this is a reconnection (use state vector for differential sync)
   */
  private async bootstrap(isReconnection: boolean = false): Promise<void> {
    // Build request with optional state vector for reconnection
    const body: { stateVector?: string } = {};

    // Requirements 6.4, 6.5: On reconnection, send state vector for differential sync
    // This allows server to send only the updates we're missing
    if (isReconnection) {
      body.stateVector = this.getStateVector();
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(`${this.apiBaseUrl}/docs/${this.docId}/sync/bootstrap`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Bootstrap failed: ${response.status} - ${errorText}`);
    }

    const data: BootstrapResponse = await response.json();

    // Apply snapshot if present (only on first connect, not on reconnection with state vector)
    // On reconnection, we already have the snapshot applied locally
    if (data.snapshot && !isReconnection) {
      const snapshotData = toUint8Array(data.snapshot);
      Y.applyUpdate(this.yDoc, snapshotData, REMOTE_ORIGIN);
      this.appliedUpdatesCount++;
    }

    // Apply updates (these are the updates we're missing)
    // Requirements 6.5, 6.6: CRDT merge handles concurrent local and remote changes
    for (const updateB64 of data.updates) {
      const updateData = toUint8Array(updateB64);
      Y.applyUpdate(this.yDoc, updateData, REMOTE_ORIGIN);
      this.appliedUpdatesCount++;
    }

    // Store metadata
    this.snapshotVersion = data.versionId || null;
    this.snapshotCreatedAt = data.meta.snapshotCreatedAt;
    this.lastUpdateAt = Date.now();

    // Reset offline edits flag after successful bootstrap
    // The local changes have been merged with remote state
    this.offlineEditsApplied = false;

    this.emit('updateApplied', data.meta.docSizeBytes);
  }

  /**
   * Handle local Y.Doc update event.
   * IMPORTANT: Must check origin to avoid echo (remote updates re-sent).
   *
   * Requirements 6.2, 6.3: Local edits are always applied to Y.Doc,
   * but only batched for sending when connected.
   */
  private handleLocalUpdate(update: Uint8Array, origin: unknown): void {
    // Ignore remote updates to prevent echo
    if (origin === REMOTE_ORIGIN) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Debug logging
        console.log('[YjsSyncManager] Ignoring remote update (REMOTE_ORIGIN)');
      }
      return;
    }

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- Debug logging
      console.log('[YjsSyncManager] handleLocalUpdate called', {
        state: this.state,
        updateSize: update.byteLength,
        originType: origin === null ? 'null' : typeof origin,
      });
    }

    // Track that we have local edits for offline awareness
    this.lastUpdateAt = Date.now();

    // Requirements 6.2, 6.3: Accept local edits even when disconnected
    // The Y.Doc state is preserved automatically, but we mark offline edits
    // for potential queuing on reconnection
    if (this.state === 'disconnected' || this.state === 'bootstrapping' || this.state === 'error') {
      // Mark that offline edits have been applied for reconnection handling
      this.offlineEditsApplied = true;
      // Keep offline updates to send after reconnection
      this.offlineUpdates.push(update.slice());
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Debug logging
        console.log('[YjsSyncManager] Update queued (offline)', { state: this.state });
      }
      return;
    }

    // Add to batcher for sending
    this.batcher.addUpdate(update);

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- Debug logging
      console.log('[YjsSyncManager] Update added to batcher');
    }

    // Transition to syncing if not already
    if (this.state === 'synced') {
      this.setState('syncing');
    }
  }

  /**
   * Handle batched message ready to send.
   */
  private handleBatchedMessage(message: YjsUpdateMessage): void {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- Debug logging
      console.log('[YjsSyncManager] handleBatchedMessage', {
        hasSendUpdate: !!this.sendUpdate,
        originalSize: message.originalSize,
        compressed: message.compressed,
      });
    }

    if (!this.sendUpdate) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Debug logging
        console.warn('[YjsSyncManager] No sendUpdate callback, dropping message');
      }
      return;
    }

    try {
      this.sendUpdate(message);
      this.sentUpdatesCount++;
      this.emit('updateSent', message.originalSize);

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Debug logging
        console.log('[YjsSyncManager] Update sent successfully', {
          sentUpdatesCount: this.sentUpdatesCount,
        });
      }

      // Transition back to synced
      if (this.state === 'syncing') {
        this.setState('synced');
      }
    } catch (err) {
      console.error('[YjsSyncManager] Failed to send update:', err);
      const error = err instanceof Error ? err : new Error(String(err));
      this.emit('error', error);
    }
  }

  /**
   * Apply remote update to local Y.Doc.
   * Uses REMOTE_ORIGIN to mark as remote for echo prevention.
   */
  private applyRemoteUpdate(update: Uint8Array): void {
    Y.applyUpdate(this.yDoc, update, REMOTE_ORIGIN);
    this.appliedUpdatesCount++;
    this.lastUpdateAt = Date.now();
    this.emit('updateApplied', update.byteLength);
  }

  /**
   * Flush offline updates collected while disconnected.
   * Merges updates to minimize payload size before enqueueing.
   */
  private flushOfflineUpdates(): void {
    if (this.offlineUpdates.length === 0) return;

    const firstUpdate = this.offlineUpdates[0];
    if (!firstUpdate) return;

    const mergedUpdate =
      this.offlineUpdates.length === 1 ? firstUpdate : Y.mergeUpdates(this.offlineUpdates);

    this.offlineUpdates = [];
    this.offlineEditsApplied = false;

    this.batcher.addUpdate(mergedUpdate);
  }

  /**
   * Set sync state and emit event.
   */
  private setState(newState: SyncState): void {
    if (this.state === newState) return;

    this.state = newState;
    this.emit('stateChange', newState);
  }

  /**
   * Set sync state with reconnection awareness.
   * Emits reconnectSynced event when transitioning to synced on reconnection.
   *
   * Requirements 6.9: Emit reconnectSynced event when recovery completes
   */
  private setStateWithReconnection(newState: SyncState, isReconnection: boolean): void {
    if (this.state === newState) return;

    const oldState = this.state;
    this.state = newState;
    this.emit('stateChange', newState);

    // Emit reconnectSynced when going from bootstrapping to synced on reconnect
    // Requirements 6.9: THE System SHALL emit `reconnect-synced` event when recovery completes
    if (oldState === 'bootstrapping' && newState === 'synced' && isReconnection) {
      this.emit('reconnectSynced');
    }
  }

  /**
   * Emit event to handlers.
   */
  private emit<K extends keyof SyncEvents>(event: K, ...args: Parameters<SyncEvents[K]>): void {
    const handlers = this.eventHandlers.get(event);
    if (!handlers) return;

    for (const handler of handlers) {
      try {
        (handler as (...args: unknown[]) => void)(...args);
      } catch (err) {
        console.error(`[YjsSyncManager] Error in ${event} handler:`, err);
      }
    }
  }
}
